import { createFileRoute, Link } from "@tanstack/react-router";
import { LocationPage } from "@/components/site/LocationPage";
import { pageFaqAdditions } from "@/data/pageFaqs";
import { findRow, formatPrice } from "@/data/pricing";
import { absoluteUrl, breadcrumbSchema, canonicalLinks, faqPageSchema, jsonLd, ogImage, ogUrl, serviceSchema, webPageSchema } from "@/lib/seo";

const PATH = "/fractional-general-manager";
const SERVICE_ID = `${absoluteUrl(PATH)}#service`;
const TITLE = "Fractional General Manager (GM) Australia | Ajility";
const DESCRIPTION = "A fractional GM is the same seat as a fractional COO: a part-time operator running the business beside the owner, for Australian firms of $3M to $30M.";
const INTRO = "A fractional general manager and a fractional COO are the same role: a senior operator who holds the operating seat part time, chairs the leadership meeting, owns the numbers and is accountable for delivery, without the salary of a full-time hire.";
const faqs = pageFaqAdditions[PATH];

export const Route = createFileRoute("/fractional-general-manager")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:card", content: "summary_large_image" },
      ...ogImage("fractional-general-manager", "Fractional general manager by Ajility"),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Fractional General Manager", path: PATH }])),
      jsonLd(webPageSchema({ path: PATH, name: TITLE, description: DESCRIPTION, mainEntityId: SERVICE_ID })),
      jsonLd({
        ...serviceSchema({
          name: "Fractional General Manager",
          description: DESCRIPTION,
          path: PATH,
          slug: "fractional-general-manager",
          serviceType: "Fractional general manager",
          offerId: ["operational-diagnostic", "full-diagnostic-roadmap", "fortnightly-rhythm-base"],
        }),
        areaServed: [
          { "@type": "Country", name: "Australia" },
          { "@type": "City", name: "Sydney" },
          { "@type": "City", name: "Central Coast" },
          { "@type": "City", name: "Newcastle" },
          { "@type": "City", name: "Melbourne" },
          { "@type": "City", name: "Perth" },
        ],
      }),
      jsonLd(faqPageSchema(PATH, faqs, SERVICE_ID)),
    ],
  }),
  component: FractionalGeneralManagerPage,
});

function FractionalGeneralManagerPage() {
  return (
    <LocationPage
      city="Australia"
      region="on site and remote"
      heading="Fractional general manager: the same seat, the name most Australian businesses use"
      lead={INTRO}
      intro={[]}
      industries={[]}
      sections={[
        {
          heading: "What a fractional GM does in a founder-led business",
          paragraphs: ["The GM is the integrator: bringing sales, finance, people and operations together to deliver the plan, so the owner can hold the big relationships and the direction. Ajility turns the plan into quarterly priorities with named owners, chairs the weekly leadership meeting, runs the scorecard and sets decision rights so managers know who can approve what. The work happens inside your business and in the tools you already use, with a weekly, monthly and quarterly rhythm your team can take over."],
        },
        {
          heading: "Full-time general manager or fractional?",
          paragraphs: ["A full-time GM suits an owner stepping out of the day to day when there is enough operating work for five days a week. A fractional GM suits one or two days a week in 90-day blocks while the seat is being defined and the rhythm built. For a business of $3M to $10M the SEEK figure is the better guide; the corporate range applies to larger companies. Define the seat first, then decide how much of the week it needs.", <>Not sure whether the seat should be permanent? Read <Link to="/insights/$slug" params={{ slug: "hire-a-general-manager-or-a-fractional-one" }} className="underline underline-offset-4">should I hire a general manager or a fractional one</Link>.</>],
          content: (
            <div className="scroll-table mt-8 overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-left text-sm text-charcoal">
                <caption className="sr-only">Full-time and fractional general manager comparison</caption>
                <thead className="bg-surface text-midnight">
                  <tr><th scope="col" className="p-4">Option</th><th scope="col" className="p-4">Scope and cost</th><th scope="col" className="p-4">Best fit</th></tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border"><th scope="row" className="p-4 align-top font-semibold">Full-time GM</th><td className="p-4 align-top">Owner stepping out of the day to day, enough work for five days, a market salary plus superannuation and on-costs: SEEK's 2026 data puts the average advertised general manager salary in Perth at $188,750, and Morgan McKinley's 2026 Sydney guide puts corporate general managers at $300,000 to $450,000.</td><td className="p-4 align-top">Best once the seat is defined.</td></tr>
                  <tr className="border-b border-border"><th scope="row" className="p-4 align-top font-semibold">Fractional GM</th><td className="p-4 align-top">One or two days a week, 90-day blocks, Ajility {formatPrice(findRow("Fortnightly rhythm base"))}.</td><td className="p-4 align-top">Best while the seat is being defined and the rhythm built.</td></tr>
                  <tr className="border-b border-border"><th scope="row" className="p-4 align-top font-semibold">Both</th><td className="p-4 align-top">Fractional first to define the seat and set up the rhythm, then a full-time hire into a defined role, with the fractional operator holding them to the plan for the first 90 days.</td><td className="p-4 align-top">The usual path for businesses of $3M to $10M.</td></tr>
                </tbody>
              </table>
              <p className="mt-3 text-sm text-steel">
                Sources: <a href="https://au.seek.com/career-advice/role/general-manager/salary/in-perth" className="underline underline-offset-4">SEEK General Manager salary data, Perth, 2026</a> (au.seek.com/career-advice/role/general-manager/salary/in-perth); <a href="https://www.morganmckinley.com/au/salary-guide/data/general-manager/sydney" className="underline underline-offset-4">Morgan McKinley 2026 Salary Guide, General Manager, Sydney</a> (morganmckinley.com/au/salary-guide/data/general-manager/sydney). Market figures, not Ajility fees.
              </p>
            </div>
          ),
        },
        {
          heading: "Help selecting and setting up a full-time GM",
          paragraphs: ["Ajility designs the seat through clear accountabilities, numbers and decision rights, helps the owner judge candidates against that role, and sets the new GM up with the plan, scorecard and weekly leadership meeting over the first 90 days. Ajility does not run the recruitment search. The search stays with a recruiter or your own process, while Ajility helps you hire into a defined seat rather than an ambiguous title."],
        },
        {
          heading: "Where Ajility works",
          paragraphs: [<>Ajility works on site in <Link to="/fractional-coo-sydney" className="underline underline-offset-4">Sydney</Link>, the <Link to="/fractional-coo-central-coast" className="underline underline-offset-4">Central Coast</Link>, <Link to="/fractional-coo-newcastle" className="underline underline-offset-4">Newcastle</Link>, <Link to="/fractional-coo-melbourne" className="underline underline-offset-4">Melbourne</Link> and <Link to="/fractional-coo-perth" className="underline underline-offset-4">Perth</Link>, and remotely elsewhere in Australia. Ajility's principal currently holds fractional general manager roles in two Perth businesses.</>],
        },
      ]}
      faqHeading="Fractional general manager questions"
      faqs={faqs}
      cta={{ heading: "Talk about the operating seat in your business", body: "A free thirty-minute fit call to discuss the role, the rhythm and the next step.", linkLabel: "Book a fit call" }}
    />
  );
}